import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import ErrorBoundary from '@/components/ErrorBoundary';
import { LegalPage, PrivacyProvider } from '@/components/Privacy';
import { TooltipProvider } from '@/design-system/ui/Tooltip';
import { createQueryClient } from '@/services/queryClient';
import { ThemeProvider } from '@/theme';

import { legalDocOf } from './paths';
import AppRoutes from './routes';
import SessionGate from './SessionGate';

/** Fora do componente: um cliente só para a vida da página, com o cache de todas as telas. */
const queryClient = createQueryClient();

/**
 * Data router (e não `<BrowserRouter>`) porque só ele permite `useBlocker` — o perfil do
 * membro segura a navegação quando há alterações não salvas. As rotas continuam
 * declaradas em `<AppRoutes />`, sob um único splat.
 */
const router = createBrowserRouter(
  [
    {
      path: '*',
      element: (
        <TooltipProvider delayDuration={300}>
          <AppRoutes />
        </TooltipProvider>
      ),
    },
  ],
  { basename: import.meta.env.VITE_BASE_PATH || '/' }
);

/**
 * Raiz da aplicação: providers globais em volta das rotas.
 *
 * O `ThemeProvider` fica por fora de tudo: o tema vale também no login e no convite.
 *
 * A `SessionGate` cuida do login e monta a store com os dados do servidor
 * (`VITE_API_URL`, obrigatória).
 *
 * O `PrivacyProvider` (aviso e preferências de cookies, documentos legais) também fica
 * por fora da sessão; os documentos têm endereço público (`/privacidade`, `/termos`,
 * `/cookies`), aberto antes de tudo.
 *
 * Provider novo (igreja selecionada, i18n) entra aqui, envolvendo `<AppRoutes />` —
 * nunca dentro de uma página — e também em `test/renderWithProviders`.
 */
export default function App() {
  const routes = <RouterProvider router={router} />;
  const legalDoc = legalDocOf(window.location.pathname);
  const app = <SessionGate>{routes}</SessionGate>;

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <PrivacyProvider>{legalDoc ? <LegalPage doc={legalDoc} /> : app}</PrivacyProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
